/* eslint-disable no-underscore-dangle */
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, Stack, Typography, Divider, TextField } from '@mui/material';
import { Icon, theme, Button } from 'bs-unified-ui';
import { Icon as ChIcon } from '~/components/common/Icon';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useGetListContent,
  useCreateContentMutation,
  useUpdateContentMutation,
  useDeleteSectionMutation,
  useCreateSectionMutation,
} from '~/services/api/content';
import { queryKeys } from '~/services/api/queryKeys';
import DataLoading from '~/components/common/DataLoading';
import EditContentDialog from './EditContentDialog';
import ConfirmDialog from './ConfirmDialog';
import SortableShortContent from './components/SortableShortContent';
import ShortContentCard from './components/ShortContentCard';

interface Content {
  id: string;
  image: string;
  title: string;
  subTitle?: string;
  body: string;
  buttonLabel: string;
  buttonLink: string;
  order: number;
}

interface Section {
  id: string;
  title: string;
  contents: Content[];
}

interface LongContentProps {
  subTitle?: string;
  title: string;
  body: string;
  buttonLabel: string;
  image: string;
  buttonLink: string;
  onEdit: () => void;
  onDelete: () => void;
}

const LongContent: React.FC<LongContentProps> = ({
  subTitle,
  title,
  body,
  buttonLabel,
  image,
  buttonLink,
  onEdit,
  onDelete,
}) => (
  <Box
    sx={{
      'display': 'flex',
      'flexDirection': { xs: 'column', md: 'row' },
      'borderRadius': '4px',
      'overflow': 'hidden',
      'position': 'relative',
      '&:hover .action-buttons': {
        opacity: 1,
      },
    }}
  >
    <Box
      sx={{
        'flex': 1,
        'display': 'flex',
        'flexDirection': 'column',
        'justifyContent': 'center',
        'gap': 1,
        'p': 3,
        'backgroundColor': '#EDF0F7',
        '& a': {
          textDecoration: 'none !important',
        },
      }}
    >
      <Typography variant='fs14' color='rgba(51, 54, 61, 0.40)'>
        {subTitle}
      </Typography>
      <Typography variant='fs20' fontWeight={700}>
        {title}
      </Typography>
      <Typography variant='fs14'>{body}</Typography>
      <Link
        to={buttonLink}
        target='_blank'
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px',
        }}
      >
        <Typography variant='fs14' fontWeight='400'>
          <Icon name='external-link' size={16} />
        </Typography>
        <Typography variant='fs14' fontWeight='600'>
          {buttonLabel}
        </Typography>
      </Link>
    </Box>
    <Box
      sx={{
        flex: 1,
      }}
    >
      <img
        style={{
          width: '100%',
          height: '100%',
        }}
        src={image}
        alt=''
      />
    </Box>

    {/* Action buttons overlay */}
    <Box
      className='action-buttons'
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        borderRadius: '4px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        opacity: 0,
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      <Button
        variant='contained'
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        sx={{
          'width': '97px',
          'backgroundColor': 'white',
          'color': `${theme.foreground.black} !important`,
          'fontWeight': 600,
          'gap': 0.5,
          '&:hover': {
            backgroundColor: 'white !important',
          },
        }}
      >
        <ChIcon name='pencil' size={16} color='#33363D' />
        Edit
      </Button>
      <Button
        variant='contained'
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        sx={{
          'width': '97px',
          'backgroundColor': 'white',
          'color': `${theme.foreground.black} !important`,
          'fontWeight': 600,
          'gap': 0.5,
          '&:hover': {
            backgroundColor: 'white !important',
          },
        }}
      >
        <ChIcon name='delete-gray' size={16} color='#33363D' />
        Delete
      </Button>
    </Box>
  </Box>
);

const ContentPage = () => {
  const queryClient = useQueryClient();
  const { data: contentData, isFetching, isLoading } = useGetListContent();
  const sectionTitleInputRef = useRef<HTMLInputElement>(null);

  const initialSections = useMemo<Section[]>(() => {
    if (!contentData) return [];

    // eslint-disable-next-line no-underscore-dangle
    return contentData.map((section: any) => ({
      id: section._id,
      title: section.name,
      contents: section.content.map((item: any, index: number) => ({
        // eslint-disable-next-line no-underscore-dangle
        id:
          item._id ||
          `temp-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}-${index}`,
        image: item.image,
        title: item.title,
        subTitle: item.sub_title,
        body: item.body_text,
        buttonLabel: item.button_label,
        buttonLink: item.button_internal_link,
        order: item.order,
      })),
    }));
  }, [contentData]);

  const [sections, setSections] = useState<Section[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  // Sensors for drag and drop
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  // Update sections when contentData changes
  useEffect(() => {
    setSections(initialSections);
  }, [initialSections]);

  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [editingContent, setEditingContent] = useState<Content | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [isLongContent, setIsLongContent] = useState<boolean>(false);
  const [isAddingContent, setIsAddingContent] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deletingContent, setDeletingContent] = useState<Content | null>(null);
  const [deleteSectionDialogOpen, setDeleteSectionDialogOpen] =
    useState<boolean>(false);
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(
    null,
  );
  const [editingSectionTitle, setEditingSectionTitle] = useState<string | null>(
    null,
  );
  const [sectionTitleValue, setSectionTitleValue] = useState<string>('');
  const [sectionTitleError, setSectionTitleError] = useState<string>('');
  const [pendingNewSection, setPendingNewSection] = useState<boolean>(false);

  const prevSectionsLengthRef = useRef<number>(0);
  const shouldFocusInputRef = useRef<boolean>(false);
  const newSectionIdRef = useRef<string | null>(null);

  // Focus the input when needed (after adding section or clicking edit)
  useEffect(() => {
    if (
      !editingSectionTitle ||
      !sectionTitleInputRef.current ||
      !shouldFocusInputRef.current
    ) {
      return () => {};
    }

    const timer = setTimeout(() => {
      sectionTitleInputRef.current?.focus();
      shouldFocusInputRef.current = false;
    }, 100);

    return () => clearTimeout(timer);
  }, [editingSectionTitle, sections.length]);

  const { mutate: createContentMutate, isPending: isCreating } =
    useCreateContentMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.content.list] });
        setEditDialogOpen(false);
      },
      onError: () => {
        // Handle error
      },
    });

  const { mutate: updateContentMutate, isPending: isUpdating } =
    useUpdateContentMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.content.list] });
        setEditDialogOpen(false);
      },
      onError: () => {
        // Handle error
      },
    });

  const { mutate: deleteSectionMutate, isPending: isDeletingSection } =
    useDeleteSectionMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.content.list] });
        setDeleteSectionDialogOpen(false);
        setDeletingSectionId(null);
      },
      onError: () => {
        // Handle error
      },
    });

  const { mutate: createSectionMutate, isPending: isCreatingSection } =
    useCreateSectionMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.content.list] });
      },
      onError: () => {
        // Handle error
      },
    });

  const handleEditContent = (
    content: Content,
    isLong: boolean,
    sectionId: string,
  ) => {
    setEditingContent(content);
    setIsLongContent(isLong);
    setEditingSectionId(sectionId);
    setIsAddingContent(false);
    setEditDialogOpen(true);
  };

  const handleAddContent = (sectionId: string) => {
    setEditingContent(null);
    setIsLongContent(false);
    setEditingSectionId(sectionId);
    setIsAddingContent(true);
    setEditDialogOpen(true);
  };

  const handleSaveContent = (values: any) => {
    if (isAddingContent && editingSectionId !== null) {
      // Calculate order for new content
      const currentSection = sections.find((s) => s.id === editingSectionId);
      let order = 1;

      if (currentSection) {
        if (isLongContent) {
          // Long content always has order 1
          order = 1;
        } else {
          // Short content: get max order from existing short contents and add 1
          const shortContents = currentSection.contents.filter(
            (c) => c.order !== 1,
          );
          order =
            shortContents.length > 0
              ? Math.max(...shortContents.map((c) => c.order)) + 1
              : 2; // Start from 2 if no short content exists (1 is reserved for long content)
        }
      }

      // Create FormData
      const formData = new FormData();
      formData.append('title', values.title || '');
      if (values.subTitle) {
        formData.append('sub_title', values.subTitle);
      }
      formData.append('body_text', values.body || '');
      formData.append('button_label', values.buttonLabel || '');
      formData.append('button_internal_link', values.buttonLink || '');
      formData.append('order', order.toString());

      // Handle image - if it's a File, append it; if it's a string URL, skip it for create
      if (values.image && values.image instanceof File) {
        formData.append('image', values.image);
      }

      // Call API to create content
      createContentMutate({
        sectionId: editingSectionId,
        params: formData,
      });
    } else if (editingContent && editingSectionId !== null) {
      // Update existing content
      // When editing content with a new image file, we need to:
      // 1. Delete the old content
      // 2. Create a new content with the new image
      // This is a workaround since the update API doesn't support file upload directly

      if (values.image instanceof File) {
        // If there's a new image file, we need to create new content
        // Use the existing content's order
        const { order } = editingContent;

        // Create FormData for new content
        const formData = new FormData();
        formData.append('title', values.title || '');
        if (values.subTitle) {
          formData.append('sub_title', values.subTitle);
        }
        formData.append('body_text', values.body || '');
        formData.append('button_label', values.buttonLabel || '');
        formData.append('button_internal_link', values.buttonLink || '');
        formData.append('order', order.toString());
        formData.append('image', values.image);

        // Create new content (which will upload the image)
        createContentMutate({
          sectionId: editingSectionId,
          params: formData,
        });
      } else {
        // No new image file, just update the section with text changes
        const currentSection = sections.find((s) => s.id === editingSectionId);
        if (!currentSection) return;

        // Update the content in the section
        const updatedContents = currentSection.contents.map((content) => {
          if (content.id === editingContent.id) {
            return {
              ...content,
              title: values.title || content.title,
              subTitle:
                values.subTitle !== undefined
                  ? values.subTitle
                  : content.subTitle,
              body: values.body || content.body,
              buttonLabel: values.buttonLabel || content.buttonLabel,
              buttonLink: values.buttonLink || content.buttonLink,
              image: values.image || content.image,
            };
          }
          return content;
        });

        // Prepare the section data for API
        const sectionData = {
          _id: currentSection.id,
          name: currentSection.title,
          status: 'active',
          content: updatedContents.map((content) => ({
            title: content.title,
            sub_title: content.subTitle || undefined,
            body_text: content.body,
            image: content.image,
            button_label: content.buttonLabel,
            button_internal_link: content.buttonLink,
            order: content.order,
          })),
        };

        updateContentMutate({
          sectionId: editingSectionId,
          params: sectionData,
        });
      }
    }
  };

  const handleDeleteContent = (content: Content, sectionId: string) => {
    setDeletingContent(content);
    setEditingSectionId(sectionId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deletingContent && editingSectionId !== null) {
      const currentSection = sections.find((s) => s.id === editingSectionId);

      if (currentSection) {
        // Filter out the deleted content
        const updatedContents = currentSection.contents.filter(
          (content) => content.id !== deletingContent.id,
        );

        // Prepare the payload for the API
        const payload = {
          _id: currentSection.id,
          name: currentSection.title,
          status: 'active',
          content: updatedContents.map((item) => ({
            title: item.title,
            sub_title: item.subTitle || '',
            body_text: item.body,
            image: item.image,
            button_label: item.buttonLabel,
            button_internal_link: item.buttonLink,
            order: item.order,
          })),
        };

        // Call the update API
        updateContentMutate(
          {
            sectionId: currentSection.id,
            params: payload,
          },
          {
            onSuccess: () => {
              setDeleteDialogOpen(false);
              setDeletingContent(null);
              setEditingSectionId(null);
            },
          },
        );
      }
    }
  };

  const handleDeleteSection = (sectionId: string) => {
    setDeletingSectionId(sectionId);
    setDeleteSectionDialogOpen(true);
  };

  const handleConfirmDeleteSection = () => {
    if (deletingSectionId) {
      deleteSectionMutate(deletingSectionId);
    }
  };

  const handleEditSectionTitle = (sectionId: string, currentTitle: string) => {
    setEditingSectionTitle(sectionId);
    setSectionTitleValue(currentTitle);
    setSectionTitleError('');
    shouldFocusInputRef.current = true;
  };

  const handleCancelEditSectionTitle = () => {
    setEditingSectionTitle(null);
    setSectionTitleValue('');
    setSectionTitleError('');
    newSectionIdRef.current = null;
  };

  const handleSaveSectionTitle = () => {
    // Validate title
    if (!sectionTitleValue.trim()) {
      setSectionTitleError('Title is required');
      return;
    }

    if (editingSectionTitle) {
      const currentSection = sections.find((s) => s.id === editingSectionTitle);

      if (currentSection) {
        // Check if title has changed
        if (currentSection.title === sectionTitleValue.trim()) {
          // No change, just close edit mode without calling API
          setEditingSectionTitle(null);
          setSectionTitleValue('');
          setSectionTitleError('');
          newSectionIdRef.current = null;
          return;
        }

        // Update existing section
        const payload = {
          _id: currentSection.id,
          name: sectionTitleValue.trim(),
          status: 'active',
          content: currentSection.contents.map((item) => ({
            title: item.title,
            sub_title: item.subTitle || '',
            body_text: item.body,
            image: item.image,
            button_label: item.buttonLabel,
            button_internal_link: item.buttonLink,
            order: item.order,
          })),
        };

        updateContentMutate(
          {
            sectionId: currentSection.id,
            params: payload,
          },
          {
            onSuccess: () => {
              setEditingSectionTitle(null);
              setSectionTitleValue('');
              setSectionTitleError('');
              newSectionIdRef.current = null;
            },
          },
        );
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = useCallback(
    (event: DragStartEvent, sectionId: string) => {
      const { active } = event;
      setActiveId(active.id as string);
      setActiveSectionId(sectionId);
    },
    [],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent, sectionId: string) => {
      const { active, over } = event;
      setActiveId(null);
      setActiveSectionId(null);

      if (!over || active.id === over.id) {
        return;
      }

      const currentSection = sections.find((s) => s.id === sectionId);
      if (!currentSection) return;

      const shortContents = currentSection.contents.filter(
        (c) => c.order !== 1,
      );
      const oldIndex = shortContents.findIndex((c) => c.id === active.id);
      const newIndex = shortContents.findIndex((c) => c.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      // Reorder using arrayMove
      const reorderedShortContents = arrayMove(
        shortContents,
        oldIndex,
        newIndex,
      );

      // Update order values (starting from 2 for short content)
      const updatedShortContents = reorderedShortContents.map(
        (item, index) => ({
          ...item,
          order: index + 2,
        }),
      );

      // Combine with long content
      const longContent = currentSection.contents.find((c) => c.order === 1);
      const allContents = longContent
        ? [longContent, ...updatedShortContents]
        : updatedShortContents;

      // Update local state immediately for smooth UX
      setSections((prevSections) =>
        prevSections.map((section) =>
          section.id === sectionId
            ? { ...section, contents: allContents }
            : section,
        ),
      );

      // Prepare payload for API
      const payload = {
        _id: currentSection.id,
        name: currentSection.title,
        status: 'active',
        content: allContents.map((item) => ({
          title: item.title,
          sub_title: item.subTitle || '',
          body_text: item.body,
          image: item.image,
          button_label: item.buttonLabel,
          button_internal_link: item.buttonLink,
          order: item.order,
        })),
      };

      // Call API to update
      updateContentMutate({
        sectionId: currentSection.id,
        params: payload,
      });
    },
    [sections, updateContentMutate],
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setActiveSectionId(null);
  }, []);

  const handleAddSection = () => {
    // Create new section via API with default title
    const payload = {
      name: 'New section',
      status: 'active',
      content: [],
    };

    // Close any existing edit mode and reset the ref to allow new section to be edited
    setEditingSectionTitle(null);
    setSectionTitleValue('');
    setSectionTitleError('');
    newSectionIdRef.current = null;
    setPendingNewSection(true);
    createSectionMutate(payload);
  };

  // Auto-edit newly created "New section" after data is fetched
  useEffect(() => {
    if (pendingNewSection && sections.length > 0 && !editingSectionTitle) {
      // Check if a new section was added
      if (sections.length > prevSectionsLengthRef.current) {
        // Find the newly created section by checking if it wasn't in the previous list
        const lastSection = sections[sections.length - 1];

        if (
          lastSection &&
          lastSection.title === 'New section' &&
          !newSectionIdRef.current
        ) {
          // Store the new section ID to track it
          newSectionIdRef.current = lastSection.id;
          setEditingSectionTitle(lastSection.id);
          setSectionTitleValue('New section');
          setSectionTitleError('');
          setPendingNewSection(false);
          shouldFocusInputRef.current = true;
        }
      }
      prevSectionsLengthRef.current = sections.length;
    } else {
      // Update ref when not in pending state
      prevSectionsLengthRef.current = sections.length;
    }
  }, [sections, pendingNewSection, editingSectionTitle]);

  const renderSection = (section: Section) => {
    // Separate long content (order === 1) and short content (order !== 1)
    const longContent = section.contents.find((content) => content.order === 1);
    const shortContents = section.contents.filter(
      (content) => content.order !== 1,
    );

    return (
      <Box
        key={section.id}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mt: 2,
        }}
      >
        {/* Section title with edit icon */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems:
              editingSectionTitle === section.id ? 'flex-start' : 'center',
            gap: 1,
          }}
        >
          {editingSectionTitle === section.id ? (
            <>
              <TextField
                inputRef={sectionTitleInputRef}
                value={sectionTitleValue}
                onChange={(e) => {
                  setSectionTitleValue(e.target.value);
                  if (sectionTitleError) {
                    setSectionTitleError('');
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveSectionTitle();
                  }
                }}
                error={!!sectionTitleError}
                helperText={sectionTitleError}
                size='small'
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '16px',
                    fontWeight: 700,
                    width: 'auto',
                  },
                }}
              />
              <Box
                component='span'
                sx={{
                  cursor: 'pointer',
                  mt: 1,
                }}
                onClick={handleSaveSectionTitle}
              >
                <ChIcon name='check-fill' color='#33363D' size={16} />
              </Box>
              <Box
                component='span'
                sx={{
                  cursor: 'pointer',
                  mt: 1,
                }}
                onClick={handleCancelEditSectionTitle}
              >
                <ChIcon name='close-fill' color='#33363D' size={16} />
              </Box>
            </>
          ) : (
            <>
              <Typography variant='fs16' fontWeight={700}>
                {section?.title}
              </Typography>
              <Box
                component='span'
                sx={{
                  cursor: 'pointer',
                }}
                onClick={() =>
                  handleEditSectionTitle(section.id, section.title)
                }
              >
                <ChIcon name='pencil' color='#33363D' size={16} />
              </Box>
              <Box
                component='span'
                sx={{
                  cursor: 'pointer',
                }}
                onClick={() => handleDeleteSection(section.id)}
              >
                <ChIcon name='delete-gray' color='#F24822' size={16} />
              </Box>
            </>
          )}
        </Box>

        {/* Long content - full width */}
        {longContent ? (
          <Box sx={{ width: '100%' }}>
            <LongContent
              subTitle={longContent.subTitle}
              title={longContent.title}
              body={longContent.body}
              buttonLabel={longContent.buttonLabel}
              image={longContent.image}
              buttonLink={longContent.buttonLink}
              onEdit={() => handleEditContent(longContent, true, section.id)}
              onDelete={() => handleDeleteContent(longContent, section.id)}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F4F5F9',
              borderRadius: '4px',
              cursor: 'pointer',
              minHeight: '240px',
            }}
            onClick={() => {
              setEditingContent(null);
              setIsLongContent(true);
              setEditingSectionId(section.id);
              setIsAddingContent(true);
              setEditDialogOpen(true);
            }}
          >
            <ChIcon name='add-line' size={24} useBackgroundImg />
            <Typography variant='fs14' fontWeight={600}>
              Add content
            </Typography>
          </Box>
        )}

        {/* Short contents - 3 columns grid with drag and drop */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(event) => handleDragStart(event, section.id)}
          onDragEnd={(event) => handleDragEnd(event, section.id)}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={shortContents.map((c) => c.id)}
            strategy={rectSortingStrategy}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr', // 1 column on mobile
                  sm: '1fr', // 1 column on small tablets
                  md: 'repeat(2, 1fr)', // 2 columns on medium screens
                  lg: 'repeat(3, 1fr)', // 3 columns on large screens
                },
                gap: 2, // 16px
              }}
            >
              {shortContents.map((content) => (
                <SortableShortContent
                  key={content.id}
                  id={content.id}
                  image={content.image}
                  title={content.title}
                  body={content.body}
                  buttonLabel={content.buttonLabel}
                  buttonLink={content.buttonLink}
                  onEdit={() => handleEditContent(content, false, section.id)}
                  onDelete={() => handleDeleteContent(content, section.id)}
                />
              ))}

              {/* Add content block - always shown */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#F4F5F9',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  minHeight: '336px',
                }}
                onClick={() => handleAddContent(section.id)}
              >
                <ChIcon name='add-line' size={24} useBackgroundImg />
                <Typography variant='fs14' fontWeight={600}>
                  Add content
                </Typography>
              </Box>
            </Box>
          </SortableContext>
          <DragOverlay adjustScale style={{ transformOrigin: '0 0' }}>
            {activeId && activeSectionId === section.id
              ? (() => {
                  const content = shortContents.find((c) => c.id === activeId);
                  if (!content) return null;
                  return (
                    <ShortContentCard
                      id={content.id}
                      image={content.image}
                      title={content.title}
                      body={content.body}
                      buttonLabel={content.buttonLabel}
                      buttonLink={content.buttonLink}
                      onEdit={() => {}}
                      onDelete={() => {}}
                      isDragging
                    />
                  );
                })()
              : null}
          </DragOverlay>
        </DndContext>
      </Box>
    );
  };

  // Show full-page loading only on initial load
  if (isLoading) {
    return <DataLoading loading={isLoading} />;
  }

  return (
    <Stack
      className='customized-scrollbar'
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        color: theme.foreground.black,
        backgroundColor: '#fbfbfc',
        position: 'relative',
      }}
    >
      {/* Overlay loading indicator when refetching */}
      {((isFetching && !isLoading) || isCreatingSection) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <DataLoading loading />
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          maxWidth: '960px',
          px: { xs: 2, sm: 2, md: 4 },
          py: 2,
          mx: 'auto',
          boxSizing: { xs: 'border-box', sm: 'border-box', md: 'content-box' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 2 },
            opacity: 0.7,
          }}
        >
          <Typography variant='fs20' fontWeight={700}>
            Hi, welcome to ABC Cannabis
          </Typography>
          <Typography
            color={theme.palette.primary.main}
            variant='fs14'
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 0.5,
            }}
          >
            <Icon
              name='external-link'
              size={16}
              color={theme.palette.primary.main}
            />
            Visit online store
          </Typography>
        </Box>
        {sections?.map((section, index) => (
          <div key={section.id}>
            {renderSection(section)}
            {index < sections.length - 1 && <Divider sx={{ my: 2 }} />}
          </div>
        ))}

        {/* Divider */}
        <Divider sx={{ my: 2 }} />

        {/* Add Section Button */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F4F5F9',
            borderRadius: '4px',
            cursor: 'pointer',
            minHeight: '120px',
          }}
          onClick={handleAddSection}
        >
          <ChIcon name='add-line' size={24} useBackgroundImg />
          <Typography variant='fs14' fontWeight={600}>
            Add section
          </Typography>
        </Box>
      </Box>

      {/* Edit Content Dialog */}
      {editDialogOpen && (
        <EditContentDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingContent(null);
          }}
          content={editingContent}
          isLongContent={isLongContent}
          onSubmit={handleSaveContent}
          isLoading={isCreating || isUpdating}
        />
      )}

      {/* Delete Content Confirmation Dialog */}
      {deleteDialogOpen && (
        <ConfirmDialog
          title='Delete content'
          content="Once deleted, the content can't be recovered. Are you sure you want to continue?"
          onCancel={() => setDeleteDialogOpen(false)}
          onOk={handleConfirmDelete}
          isLoading={isUpdating}
        />
      )}

      {/* Delete Section Confirmation Dialog */}
      {deleteSectionDialogOpen && (
        <ConfirmDialog
          title='Delete section'
          content="Once deleted, the section can't be recovered. Are you sure you want to continue?"
          onCancel={() => setDeleteSectionDialogOpen(false)}
          onOk={handleConfirmDeleteSection}
          isLoading={isDeletingSection}
        />
      )}
    </Stack>
  );
};
export default ContentPage;
